interface CompressParams {
  metadata?: boolean;
}

const config = {
  urls: {
    processUrl: 'https://process.filestackapi.com',
    fileApiUrl: 'https://www.filestackapi.com/api/file',
    uploadApiUrl: 'https://upload.filestackapi.com',
    cloudApiUrl: 'https://cloud.filestackapi.com',
    cdnUrl: 'https://cdn.filestackcontent.com',
  },
};

export class ImageClientService {
  private source: string | string[];
  private readonly apikey: string;
  private transforms: any = [];
  private b64: boolean = false;
  private cname: string = '';
  private customDomain: string | undefined;

  constructor(source: string | string[], apikey: string) {
    this.apikey = apikey;
    this.source = source;
  }

  noMetadata() {
    return this.addTask('no_metadata', true);
  }

  autoImage() {
    return this.addTask('auto_image', true);
  }

  rotate() {
    return this.addTask('rotate=deg:exif', true);
  }

  addTask(name: string, params?) {
    if (name !== 'cache' && typeof params === 'boolean') {
      if (!params) {
        return this;
      } else {
        params = undefined;
      }
    } else if (typeof params === 'object' && !Object.keys(params).length) {
      params = undefined;
    }

    this.transforms.push({ name, params });
    return this;
  }

  compress(params?: CompressParams) {
    return this.addTask('compress', params || true);
  }

  resize(params: { width?: number; height?: number }) {
    return this.addTask('resize', params);
  }

  reset() {
    this.transforms = [];
    return this;
  }

  monochrome() {
    return this.addTask('monochrome', true);
  }

  sepia(params?: any | boolean) {
    return this.addTask('sepia', params);
  }

  negative() {
    return this.addTask('negative', true);
  }

  oilPaint(params?: any | boolean) {
    return this.addTask('oil_paint', params);
  }

  blur(params?: any | boolean) {
    return this.addTask('blur', params);
  }

  toString() {
    const returnUrl: any = [];
    returnUrl.push(this.getCdnHost());

    if (this.apikey) {
      returnUrl.push(this.apikey);
    }

    let transformsString = this.generateTransformString();

    let source = this.source;

    if (this.b64) {
      if (this.transforms.length > 0) {
        transformsString = `b64/${b64(JSON.stringify(this.transforms), true)}`;
      }

      if (Array.isArray(source)) {
        source = this.arrayToString(source);
      }

      source = `b64://${b64(source, true)}`;
    } else {
      if (Array.isArray(source)) {
        source = this.arrayToString(source);
      } else {
        source = this.escapeValue(source);
      }
    }

    if (transformsString.length) {
      returnUrl.push(transformsString);
    }

    returnUrl.push(source);
    return returnUrl.join('/');
  }

  private getCdnHost(): string {
    let urls = Object.assign({}, config.urls);

    if (this.customDomain) {
      urls.cdnUrl = this.customDomain;
    }

    urls = resolveHost(urls as any, this.cname);

    return urls.cdnUrl;
  }

  private generateTransformString(): string {
    let transforms: any = [];

    this.transforms.forEach(el => {
      transforms.push(this.optionToString(el.name, el.params));
    });

    return transforms.join('/');
  }

  private escapeValue(value: string): string {
    if (typeof value !== 'string') {
      return value;
    }

    if (value.indexOf('/') > -1 || value.indexOf(',') > -1) {
      return `"${value}"`;
    }

    return value;
  }

  private arrayToString(arr: any[]): string {
    const toReturn = arr.map(el => {
      if (Array.isArray(el)) {
        return this.arrayToString(el);
      }

      return this.escapeValue(el);
    });

    return `[${toReturn}]`;
  }

  private optionToString(key: string, values: any): string {
    let optionsString: string[] = [];
    if (typeof values === 'undefined') {
      return key;
    }

    // if we just want to enable feature
    if (typeof values === 'boolean') {
      if (!values && key === 'cache') {
        return 'cache=false';
      }

      return key;
    }

    Object.keys(values).forEach(i => {
      if (Array.isArray(values[i])) {
        optionsString.push(`${i}:${this.arrayToString(values[i])}`);
        return;
      }

      optionsString.push(`${i}:${this.escapeValue(values[i])}`);
    });

    return `${key}=${optionsString.join(',')}`;
  }
}

const b64 = (data: string, safeUrl: boolean = false): string => {
  const b64 = Buffer.from(data).toString('base64');

  if (safeUrl) {
    return b64.replace(/\//g, '_').replace(/\+/g, '-');
  }

  return b64;
};

const resolveHost = (urls: any, cname: string): any => {
  if (!cname) {
    return urls;
  }

  const hosts = /filestackapi.com|filestackcontent.com/i;

  Object.keys(urls).forEach(key => {
    urls[key] = urls[key].replace(hosts, cname);
  });

  return urls;
};
